import { IPurchase, PurchasesStore } from "./types";
import { comparePurchase } from "./utils";
import { useMutation, useQuery, useQueryClient } from "@common/utils/use-query";

export const purchasesStore: PurchasesStore = {
  // Public endpoint, rate limited
  useCheckDNSAvailability(dns: string, { enabled } = { enabled: false }) {
    const res = useQuery(`/account/instance_dns_available?dns_alias=${dns}`, {
      enabled,
    });

    if (res.error?.status === 409) {
      res.error.message = "Looks like that DNS alias is already taken.";
    } else if (res.error?.status === 429) {
      res.error.message = "Too many requests! Please try again later.";
    }

    return res;
  },

  // User needs to be authenticated (the account already exists)
  useCheckDNSAvailabilityWithAccount(
    dns_alias,
    { enabled } = { enabled: false },
  ) {
    const res = useQuery(
      `/account/purchases/action/instance_dns_available?dns_alias=${dns_alias}`,
      {
        enabled,
      },
    );

    if (res.error?.status === 409) {
      res.error.message = "Looks like that DNS alias is already taken.";
    }

    return res;
  },

  useCreatePurchase() {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: "/account/purchases",
        method: "POST",
      },
      {
        onError(error) {
          if (
            error.message.indexOf("already running") > -1 &&
            error.message.indexOf("trial") > -1
          ) {
            error.status = 402;
          }
        },
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: ["/account/purchases"],
          });
        },
      },
    );
  },

  useCreateDevPurchase() {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: "/account/purchases/dev",
        method: "POST",
      },
      {
        onError(error) {
          if (
            error.message.indexOf("already running") > -1 &&
            error.message.indexOf("trial") > -1
          ) {
            error.status = 402;
          }
        },
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: ["/account/purchases"],
          });
        },
      },
    );
  },

  useGetPurchases({ enabled } = { enabled: true }) {
    const queryClient = useQueryClient();
    const res = useQuery<unknown, unknown, IPurchase[]>("/account/purchases", {
      enabled,
    });

    if (res.data) {
      queryClient.setQueryData(
        ["/account/purchases"],
        res.data.sort(comparePurchase),
      );
    }

    return res;
  },

  useGetPurchase(id, { enabled } = { enabled: true }) {
    const queryClient = useQueryClient();
    const res = useQuery<unknown, unknown, IPurchase>(
      `/account/purchases/${id}`,
      {
        enabled,
      },
    );

    if (res.data) {
      const purchases =
        queryClient.getQueryData<IPurchase[]>(["/account/purchases"]) || [];
      const purchasesCopy = [...purchases];

      // update the purchases list
      const purchaseIndex = purchases?.findIndex(
        ({ id }) => id === res.data.id,
      );
      if (purchaseIndex && purchaseIndex > -1) {
        purchasesCopy[purchaseIndex] = res.data;
      }
      // add the new purchase to the list
      else {
        purchasesCopy.push(res.data);
      }

      // save
      queryClient.setQueryData(
        ["/account/purchases"],
        purchasesCopy.sort(comparePurchase),
      );
    }

    return res;
  },

  useMigrateInstance(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/initiate_instance_migration`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useCancelMigration(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/cancel_instance_migration`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useCancelPurchase(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}`,
        method: "DELETE",
      },
      {
        onSuccess() {
          queryClient.refetchQueries({
            queryKey: [`/account/purchases/${id}`],
          });
          return queryClient.refetchQueries({
            queryKey: ["/account/purchases"],
          });
        },
      },
    );
  },

  useSendCancellationSurvey(id) {
    return useMutation({
      url: `/account/purchases/${id}/cancellation_survey`,
      method: "POST",
    });
  },

  useReactivatePurchase(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/resurrect`,
        method: "PUT",
      },
      {
        onError() {
          queryClient.refetchQueries({
            queryKey: [`/account/purchases/${id}`],
          });
          return queryClient.refetchQueries({
            queryKey: ["/account/purchases"],
          });
        },
        onSuccess() {
          queryClient.refetchQueries({
            queryKey: [`/account/purchases/${id}`],
          });
          return queryClient.refetchQueries({
            queryKey: ["/account/purchases"],
          });
        },
      },
    );
  },

  useChangeInstanceDNSAlias(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/change_instance_dns_alias`,
        method: "PUT",
      },
      {
        onError(error) {
          if (error.status === 409 || error.status === 400) {
            error = {
              message: "Looks like that DNS alias is already taken.",
              status: 409,
            };
          }
        },
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useChangeInstanceDomain(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/change_instance_custom_domain`,
        method: "PUT",
      },
      {
        onError(error) {
          if (error.status === 409 || error.status === 400) {
            error = {
              message: "Looks like that custom domain is already taken.",
              status: 409,
            };
          }
        },
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useChangeInstanceRegion(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/change_region`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useChangeInstanceIPAllowlist(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/change_instance_ip_allowlist`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useTryProPlan(id) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/trial_up`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useChangePlan(id: string) {
    const queryClient = useQueryClient();
    return useMutation(
      {
        url: `/account/purchases/${id}/action/change_plan`,
        method: "PUT",
      },
      {
        onSuccess() {
          return queryClient.invalidateQueries({
            queryKey: [`/account/purchases/${id}`],
          });
        },
      },
    );
  },

  useGetPlanChangeEstimate(id) {
    return useMutation({
      url: `/account/purchases/${id}/action/change_plan_preview`,
      method: "PUT",
    });
  },
};
